import { gam } from '@cbsinteractive/avia-js-gam';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';

/**
 * @copyright © Paramount Streaming
 * @description Google Ad Manager (GAM) plugin integration for VTG Avia player
 *
 * **Ad Integration Function:**
 * This plugin integrates Google Ad Manager for client-side ad insertion.
 * GAM handles ad targeting, bidding, and delivery for video content
 * across web and mobile platforms.
 *
 * **Content-Based Ad Logic:**
 * The plugin determines ad insertion method based on content type:
 * - **Standard Content**: Uses GAM for client-side ad insertion with full targeting
 * - **Special Events**: Disables GAM for CLID3-only linear ad tracking (server-side tracked via ID3)
 * - **Live Streams**: Uses different ad handling based on stream type and requirements
 *
 * **VTG Avia Integration:**
 * Uses VTG's avia-js-gam plugin (v2.51.4+) for Google Ad Manager integration
 * while maintaining Smart Tag's control over ad insertion decisions.
 *
 * @see {@link https://developers.google.com/ad-manager} Google Ad Manager Documentation
 */
export class GamPlugin extends AviaPlugin implements IAviaPlugin {
	package = gam;
	namespaceKey = 'plugin_gam';

	/**
	 * @description Initializes Google Ad Manager plugin for VTG Avia player integration
	 *
	 * **Plugin Configuration:**
	 * Sets up GAM plugin with Smart Tag namespace and VTG Avia package integration.
	 * Configures ad handling based on content and business rules.
	 *
	 * @param {ITagAbstractClassArgument} argument Tag instance for business logic integration
	 */
	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'GamPlugin'
			}
		);
	}

	/**
	 * @description Determines if Google Ad Manager should be used based on content ad insertion strategy
	 *
	 * **Ad Insertion Decision Logic:**
	 * This method implements Paramount's dual ad insertion strategy that optimizes revenue
	 * while avoiding ad conflicts between client-side and server-side insertion methods.
	 *
	 * **Content Classification:**
	 *
	 * **✅ Use GAM (Client-Side Ads):**
	 * - Standard VOD content requiring targeted ad insertion
	 * - Live streams without pre-inserted ad breaks
	 * - Content where user targeting and real-time bidding maximize revenue
	 *
	 * **❌ Skip GAM (Server-Side Ads):**
	 * - Special events with LINEAR_AD_TRACKING_PLUGIN (CLID3-only linear ad tracking)
	 * - Content with pre-inserted ad breaks from content providers
	 * - Streams where server-side ad insertion is already optimized
	 *
	 * **Business Impact:**
	 * - **Prevents Ad Conflicts**: Avoids double ad insertion that degrades user experience
	 * - **Revenue Optimization**: Uses appropriate ad strategy for each content type
	 * - **Event Coverage**: Ensures special events use optimized server-side ad workflows
	 * - **Technical Stability**: Prevents client-side ad SDK conflicts with server-side insertion
	 *
	 * **Technical Implementation:**
	 * Checks MPX metadata for LINEAR_AD_TRACKING_PLUGIN in videoProperties array,
	 * which indicates server-side ad insertion is already configured for the content.
	 *
	 * @returns {boolean} True if GAM plugin should be loaded for client-side ad monetization
	 * @see {@link TagContentUtilities.isLinearAdTrackable} Linear ad tracking detection logic
	 */
	shouldBeUsed() {
		// CLID3-only streams => AdSetupDefinition.LINEAR_AD_TRACKING => do not load GAM plugin
		// CLID3 + DAI streams => AdSetupDefinition.DAI_* => load GAM plugin
		// INFO: CLID3 + DAI streams also contain MPXVideoProperty.LINEAR_AD_TRACKING_PLUGIN, but LINEAR_AD_TRACKING is NOT chosen for ad setup definition
		const adSetupDefinition = this.tag?.model?.adUtilities?.adSetupInfo?.()?.setup?.definition;
		return adSetupDefinition !== AdSetupDefinition.LINEAR_AD_TRACKING;
	}

	/**
	 * @description Provides Google Ad Manager configuration parameters for VTG Avia player
	 *
	 * **Configuration Strategy:**
	 * Inherits base plugin configuration from AviaPlugin parent class and extends
	 * with GAM-specific settings required for Google Ad Manager integration.
	 *
	 * **VTG Integration:**
	 * Returns configuration object compatible with VTG's avia-js-gam plugin,
	 * enabling seamless ad monetization within the VTG player ecosystem.
	 *
	 * @returns {Record<string, any>} GAM plugin configuration for VTG Avia player
	 */
	getFactoryParams() {
		return {
			...super.getFactoryParams()
		};
	}
}